import { z } from 'zod';
import { baseComponentSchema, buttonSchema, imageCropSchema } from '../component-base.schemas';

// Hero background image schema
export const heroBackgroundImageSchema = z
  .object({
    enabled: z.boolean().default(false),
    url: z.string().url('Invalid image URL').optional(),
    cloudfrontUrl: z.string().url('Invalid CloudFront URL').optional(),
    alt: z.string().max(200, 'Alt text too long').optional(),

    // Image display options
    position: z
      .enum([
        'center',
        'top',
        'bottom',
        'left',
        'right',
        'top-left',
        'top-right',
        'bottom-left',
        'bottom-right',
      ])
      .default('center')
      .optional(),
    size: z.enum(['cover', 'contain', 'auto']).default('cover').optional(),
    repeat: z.enum(['no-repeat', 'repeat', 'repeat-x', 'repeat-y']).default('no-repeat').optional(),

    // Overlay for text readability
    overlay: z
      .object({
        enabled: z.boolean().default(true),
        color: z
          .string()
          .regex(/^#[0-9A-Fa-f]{6}$/)
          .default('#000000')
          .optional(),
        opacity: z.number().min(0).max(1).default(0.5).optional(),
      })
      .optional(),

    // Parallax effect
    parallax: z.boolean().default(false).optional(),

    // Image crop settings
    crop: imageCropSchema,
  })
  .refine(
    data => {
      // Validation: if enabled, at least one URL must be provided
      if (data.enabled) {
        return !!(data.url || data.cloudfrontUrl);
      }
      return true;
    },
    {
      message: 'Either url or cloudfrontUrl must be provided when background image is enabled',
    }
  )
  .optional();

// Hero custom colors schema
export const heroCustomColorsSchema = z
  .object({
    background: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    text: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    title: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    subtitle: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    tagline: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    buttonBg: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    buttonText: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
  })
  .optional();

export const heroDataSchema = z.object({
  tagline: z.string().max(100).optional(),
  title: z.string().min(1, 'Hero title is required').max(200),
  subtitle: z.string().max(300).optional(),
  buttons: z.array(buttonSchema).default([]),

  // Enhanced background customization
  backgroundColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .nullable()
    .optional(),
  backgroundImage: heroBackgroundImageSchema,

  textColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .nullable()
    .optional(),
  alignment: z.enum(['left', 'center', 'right']).default('center'),
  size: z.enum(['small', 'medium', 'large']).default('large'),
  customColors: heroCustomColorsSchema,
});

// Split Hero image schema
export const splitHeroImageSchema = z
  .object({
    enabled: z.boolean().default(true),
    url: z.string().url('Invalid image URL').optional(),
    cloudfrontUrl: z.string().url('Invalid CloudFront URL').optional(),
    alt: z.string().max(200, 'Alt text too long').optional(),
    position: z
      .enum([
        'center',
        'top',
        'bottom',
        'left',
        'right',
        'top-left',
        'top-right',
        'bottom-left',
        'bottom-right',
      ])
      .default('center')
      .optional(),
    size: z.enum(['cover', 'contain', 'auto']).default('cover').optional(),
    repeat: z.enum(['no-repeat', 'repeat', 'repeat-x', 'repeat-y']).default('no-repeat').optional(),

    // Image crop settings
    crop: imageCropSchema,
  })
  .refine(
    data => {
      if (data.enabled) {
        return !!(data.url || data.cloudfrontUrl);
      }
      return true;
    },
    {
      message: 'Either url or cloudfrontUrl must be provided when image is enabled',
    }
  )
  .optional();

/** Hex color or CSS linear/radial gradient (for continuous cutout backgrounds) */
const splitHeroBackgroundValueSchema = z
  .string()
  .refine(
    val =>
      /^#[0-9A-Fa-f]{6}$/.test(val) ||
      val.startsWith('linear-gradient') ||
      val.startsWith('radial-gradient'),
    { message: 'Must be a hex color or CSS gradient' }
  )
  .nullable()
  .optional();

// Split Hero custom colors schema
export const splitHeroCustomColorsSchema = z
  .object({
    background: splitHeroBackgroundValueSchema,
    text: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    title: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    subtitle: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    tagline: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    buttonBg: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    buttonText: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
  })
  .optional();

// Split Hero data schema
export const splitHeroDataSchema = z
  .object({
    tagline: z.string().max(100).optional(),
    title: z.string().min(1, 'Split Hero title is required').max(200),
    subtitle: z.string().max(1000).optional(),
    buttons: z.array(buttonSchema).default([]),
    image: splitHeroImageSchema,
    layout: z.enum(['image-left', 'image-right']).default('image-right'),
    alignment: z.enum(['left', 'center', 'right']).default('left'),
    size: z.enum(['small', 'medium', 'large']).default('large'),
    /** fill = photo panel (default); cutout = continuous bg + foreground subject */
    imagePresentation: z.enum(['fill', 'cutout']).default('fill'),
    backgroundColor: splitHeroBackgroundValueSchema,
    textColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    imageWidth: z.number().int().min(20).max(80).default(50),
    textWidth: z.number().int().min(20).max(80).default(50),
    stackOnMobile: z.boolean().default(true),
    mobileImageFirst: z.boolean().default(false),
    customColors: splitHeroCustomColorsSchema,
  })
  .refine(
    data => {
      // Image width + text width should equal 100
      return data.imageWidth + data.textWidth === 100;
    },
    {
      message: 'Image width and text width must add up to 100%',
    }
  );

// Simple Header custom colors schema
export const simpleHeaderCustomColorsSchema = z
  .object({
    background: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    text: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    title: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    subtitle: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    buttonBg: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
    buttonText: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
      .optional(),
  })
  .optional();

// Simple Header data schema
export const simpleHeaderDataSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200, 'Title is too long'),
  subtitle: z.string().max(300, 'Subtitle is too long').optional(),
  alignment: z.enum(['left', 'center', 'right']).default('center'),
  size: z.enum(['small', 'medium', 'large']).default('medium'),
  backgroundColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .nullable()
    .optional(),
  textColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .nullable()
    .optional(),
  customColors: simpleHeaderCustomColorsSchema,
  // Optional call-to-action buttons
  buttons: z.array(buttonSchema).optional(),
});

export const heroComponentSchema = baseComponentSchema.extend({
  type: z.literal('hero'),
  data: heroDataSchema,
});

export const splitHeroComponentSchema = baseComponentSchema.extend({
  type: z.literal('split-hero'),
  data: splitHeroDataSchema,
});

export const simpleHeaderComponentSchema = baseComponentSchema.extend({
  type: z.literal('simple-header'),
  data: simpleHeaderDataSchema,
});

// Type exports
export type HeroData = z.infer<typeof heroDataSchema>;
export type SplitHeroData = z.infer<typeof splitHeroDataSchema>;
export type SimpleHeaderData = z.infer<typeof simpleHeaderDataSchema>;
export type HeroComponent = z.infer<typeof heroComponentSchema>;
export type SplitHeroComponent = z.infer<typeof splitHeroComponentSchema>;
export type SimpleHeaderComponent = z.infer<typeof simpleHeaderComponentSchema>;
